import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import patientInfoService from "../patientInfo/patientInfoService";
import PointsBankAPI from "../points/PointsBankAPI";
import "../shared/PageWrapper.css";

export default function StaffPatientRegistration() {
  const navigate = useNavigate();

  const patientService = useMemo(() => new patientInfoService(), []);
  const pointsBank = useMemo(() => new PointsBankAPI(), []);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    sex: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const cleanNamePart = (value) => {
    return String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
  };

  const previewUsername = useMemo(() => {
    const first = cleanNamePart(formData.firstName);
    const last = cleanNamePart(formData.lastName);

    if (!first || !last) return "";

    return `${first}.${last}`;
  }, [formData.firstName, formData.lastName]);

  const previewEmail = previewUsername ? `${previewUsername}@newupt.com` : "";

  const previewPassword = useMemo(() => {
    if (!formData.dateOfBirth) return "";

    const [year, month, day] = formData.dateOfBirth.split("-");

    if (!year || !month || !day) return "";

    return `${month}${day}${year}`;
  }, [formData.dateOfBirth]);

  const calculateAge = (dob) => {
    if (!dob) return "";

    const birthDate = new Date(dob);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age;
  };

  const previewAge = calculateAge(formData.dateOfBirth);

  const handleChange = (e) => {
    const { id, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));

    setError("");
    setResult(null);
  };

  const validateForm = () => {
    if (!formData.firstName.trim()) {
      return "First name is required.";
    }

    if (!formData.lastName.trim()) {
      return "Last name is required.";
    }

    if (!formData.dateOfBirth) {
      return "Birthday is required.";
    }

    if (!formData.sex) {
      return "Sex is required.";
    }

    if (!previewUsername) {
      return "First and last name must contain letters or numbers.";
    }

    return "";
  };

  const createPointsBankForPatient = async (patientId) => {
    if (!patientId) {
      throw new Error("Cannot create points bank without patient ID.");
    }

    await pointsBank.addPointsEntry({
      patientId,
      totalPoints: 0,
    });

    await pointsBank.addFromAppointment(patientId, 5);
  };

  const goToRegisterInsurance = () => {
    if (!result?.patientId) {
      setError("No registered patient ID found.");
      return;
    }

    const patientName =
      result.patientName ||
      `${result.firstName || ""} ${result.lastName || ""}`.trim();

    const params = new URLSearchParams({
      patientId: String(result.patientId),
      patientName: patientName || `Patient #${result.patientId}`,
    });

    navigate(`/register/insurance-patient?${params.toString()}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm();

    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setError("");
    setResult(null);

    try {
      const payload = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        dateOfBirth: formData.dateOfBirth,
        sex: formData.sex,
      };

      const response = await patientService.registerPatientByStaff(payload);

      if (!response?.patientId) {
        throw new Error("Patient was created, but no patient ID was returned.");
      }

      await createPointsBankForPatient(response.patientId);

      setResult({
        ...response,
        firstName: response.firstName || payload.firstName,
        lastName: response.lastName || payload.lastName,
        patientName:
          response.patientName || `${payload.firstName} ${payload.lastName}`,
        pointsBankCreated: true,
      });

      setFormData({
        firstName: "",
        lastName: "",
        dateOfBirth: "",
        sex: "",
      });
    } catch (err) {
      setError(err?.message || "Staff patient registration failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pageWrapper">
      <div className="formContainer">
        <h2>Staff Patient Registration</h2>

        <p style={{ marginBottom: "1rem", color: "#555" }}>
          Use this form for staff-created patient accounts. No captcha is
          required. Contact preferences are automatically saved as no. A points
          bank is also created automatically.
        </p>

        {error && (
          <div
            style={{
              padding: "10px",
              marginBottom: "1rem",
              borderRadius: "8px",
              background: "#ffe5e5",
              color: "#9b1c1c",
              border: "1px solid #f5b5b5",
            }}
          >
            {error}
          </div>
        )}

        {result && (
          <div
            style={{
              padding: "12px",
              marginBottom: "1rem",
              borderRadius: "8px",
              background: "#e7f8ec",
              color: "#155724",
              border: "1px solid #b9e8c4",
            }}
          >
            <h3 style={{ marginTop: 0 }}>Patient Registered Successfully</h3>

            <p>
              <strong>Patient ID:</strong> {result.patientId}
            </p>

            <p>
              <strong>Username:</strong> {result.username}
            </p>

            <p>
              <strong>Password:</strong> {result.defaultPassword}
            </p>

            <p>
              <strong>Email:</strong> {result.email}
            </p>

            <p>
              <strong>Phone:</strong> {result.phoneNumber}
            </p>

            <p>
              <strong>Points Bank:</strong>{" "}
              {result.pointsBankCreated ? "Created" : "Not created"}
            </p>

            <button
              type="button"
              onClick={goToRegisterInsurance}
              style={{
                marginTop: "12px",
                width: "100%",
                padding: "12px",
                borderRadius: "8px",
                border: "none",
                cursor: "pointer",
                fontWeight: "bold",
              }}
            >
              Register Insurance for This Patient
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} autoComplete="off">
          <div className="input-container">
            <input
              type="text"
              id="firstName"
              placeholder="Enter First Name"
              value={formData.firstName}
              onChange={handleChange}
              required
              autoComplete="off"
            />
            <span className="tooltip">Patient first name.</span>
          </div>

          <div className="input-container">
            <input
              type="text"
              id="lastName"
              placeholder="Enter Last Name"
              value={formData.lastName}
              onChange={handleChange}
              required
              autoComplete="off"
            />
            <span className="tooltip">Patient last name.</span>
          </div>

          <div className="input-container">
            <input
              type="date"
              id="dateOfBirth"
              value={formData.dateOfBirth}
              onChange={handleChange}
              required
              autoComplete="off"
            />
            <span className="tooltip">
              Birthday is used to create the default password.
            </span>
          </div>

          <div className="input-container">
            <select
              id="sex"
              value={formData.sex}
              onChange={handleChange}
              required
            >
              <option value="" disabled>
                Select Sex
              </option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
            <span className="tooltip">Select patient sex.</span>
          </div>

          <div
            style={{
              padding: "12px",
              marginBottom: "1rem",
              borderRadius: "8px",
              background: "#f7f7f7",
              border: "1px solid #ddd",
              textAlign: "left",
            }}
          >
            <h3 style={{ marginTop: 0 }}>Auto-Created Account Preview</h3>

            <p>
              <strong>Username:</strong> {previewUsername || "first.last"}
            </p>

            <p>
              <strong>Password:</strong> {previewPassword || "MMDDYYYY"}
            </p>

            <p>
              <strong>Email:</strong> {previewEmail || "first.last@newupt.com"}
            </p>

            <p>
              <strong>Phone:</strong> 7185025271
            </p>

            <p>
              <strong>Age:</strong>{" "}
              {previewAge !== "" ? previewAge : "Auto-calculated"}
            </p>

            <p>
              <strong>Contact Preferences:</strong> All No
            </p>

            <p>
              <strong>Points Bank:</strong> Will be created automatically
            </p>
          </div>

          <button type="submit" disabled={submitting}>
            {submitting ? "Registering..." : "Register Patient"}
          </button>
        </form>
      </div>
    </div>
  );
}